/**
 * About Component — RILE Connect Platform Page
 *
 * Shown at /about in platform mode (no resolved ecosystem tenant). Describes
 * the RILE Connect platform itself: its origins, leadership, institutional
 * lineage, team, and governance model.
 *
 * The hero reuses the shared PlatformHero (gradient background, constellation
 * SVG, amber eyebrow, accent CTA button) so the apex pages read as a designed
 * system.
 *
 * Route: /about (platform apex only)
 *
 * Sibling apex pages: `Landing` (`/`) and `Mission` (`/mission`). This page
 * paraphrases the whitepaper more heavily than either, so it carries the same
 * shared `CitationFooter` they do; CC BY-SA 4.0 requires the attribution.
 */
import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';

import SEO from '@/components/common/SEO';
import TeamSection from '@/features/platform/team/TeamSection';
import './About.css';
import Hero from '@/components/PlatformHero/PlatformHero';

import CitationFooter from './CitationFooter';

export default function About() {
    return (
        <>
            <SEO
                title="About"
                description="Learn about RILE Connect — the open, replicable platform for making STEM pathways visible, navigable, and equitable."
                breadcrumbs={[
                    { name: 'Home', path: '/' },
                    { name: 'About', path: '/about' },
                ]}
            />

            <Hero
                pageTitle="About RILE Connect"
                eyebrow="The platform's origins, leadership, and institutional home"
                lead={
                    'RILE Connect is the operational realization of a vision laid ' +
                    'out in the COSTEM working whitepaper: a shared, open-source ' +
                    'platform that any learning ecosystem can adopt.'
                }
                primaryCta={{
                    to: '/ecosystems',
                    label: 'Browse Ecosystems',
                }}
                secondaryCta={{
                    to: '/mission',
                    label: 'Read the Mission',
                }}
            />

            <Container className="py-5">
                {/* ── The current project ──────────────────────────────── */}
                <section className="mb-5">
                    <Row className="justify-content-center">
                        <Col xs={12} md={10} lg={8}>
                            <h2 className="text-primary mb-4">
                                The Current Project
                            </h2>
                            <p>
                                RILE Connect is currently funded by the{' '}
                                <a
                                    href="https://www.coloradomatters.org"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Colorado Matters
                                </a>{' '}
                                initiative through the SIPA GovGrants Program,
                                housed in the Department of Computer Sciences at
                                Metropolitan State University of Denver. The
                                first hosted ecosystem is the{' '}
                                <strong>
                                    Colorado STEM Ecosystem (COSTEM)
                                </strong>{' '}
                                — a cross-sector coalition dedicated to
                                equitable STEM pathways from classroom to
                                career.
                            </p>
                            <p>
                                Within the scope of this grant, RILE Connect
                                builds and maintains the digital infrastructure
                                for COSTEM. The longer-term goal is to make it
                                replicable — so other regions and states can
                                stand up their own hosted ecosystems under the
                                same platform.
                            </p>
                        </Col>
                    </Row>
                </section>

                {/* ── The conceptual lineage ───────────────────────────── */}
                <section className="mb-5">
                    <Row className="justify-content-center">
                        <Col xs={12} md={10} lg={8}>
                            <h2 className="text-primary mb-4">
                                The Conceptual Lineage
                            </h2>
                            <p>
                                The concept behind COSTEM was started by Dr.
                                Janelle Johnson in a 2019 National Science
                                Foundation proposal. The key idea: most of the
                                individual pieces needed for thriving STEM
                                pathways already exist in a region, but they are
                                invisible to one another. Making those
                                connections visible — and lowering the friction
                                for acting on them — is where the platform
                                creates value.
                            </p>
                            <p>
                                The original Kumu.io-based network map that RILE
                                Connect modernizes was built by Joshua Kumin and
                                co-authored alongside the project&apos;s
                                foundational whitepaper. RILE Connect takes that
                                lineage and adds infrastructure: persistent
                                profiles, graph relationships, opportunity
                                tracking, mentor discovery, and an AI assistant
                                — all governed by the same equity-first design
                                principles that motivated the original concept.
                            </p>
                        </Col>
                    </Row>
                </section>
            </Container>

            {/* ── Team ─────────────────────────────────────────────────── */}
            <div className="about-team-wrapper">
                <TeamSection />
            </div>

            <Container className="py-5">
                {/* ── Governance and openness ──────────────────────────── */}
                <section className="mb-5">
                    <Row className="justify-content-center">
                        <Col xs={12} md={10} lg={8}>
                            <h2 className="text-primary mb-4">
                                Governance and Openness
                            </h2>
                            <p>
                                RILE Connect is released under a Creative
                                Commons Attribution-ShareAlike 4.0 license.
                                Anyone can fork the platform, stand up their own
                                hosted ecosystem, and contribute improvements
                                back to the community — subject only to
                                attribution and the same open terms.
                            </p>
                            <p>
                                For full documentation, architecture decisions,
                                and contribution guidelines, see the{' '}
                                <a
                                    href="https://github.com/msu-denver/rile-connect-website"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    RILE Connect GitHub repository
                                </a>
                                .
                            </p>
                        </Col>
                    </Row>
                </section>
            </Container>

            <CitationFooter />
        </>
    );
}
